import { Link } from "react-router-dom";
import { ChevronRight, Download } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { AppIcon } from "@/components/apps/AppIcon";
import { AppGrid } from "@/components/apps/AppGrid";
import { NoveltyBanner } from "@/components/apps/NoveltyBanner";
import { MegaScroller } from "@/components/apps/MegaScroller";
import { PlatformBadge, PlatformDownload } from "@/components/apps/Platform";
import { RatingBadge } from "@/components/reviews/Stars";
import { MinisiteBadge, MinisiteTags, useMinisiteTexts } from "@/components/apps/MinisiteInfo";

// Single mini-site: a featured hero card (many → regular grid).
const Featured = ({ app }) => {
  const { t, fmtDownloads } = useI18n();
  const { tags, features } = useMinisiteTexts(app);
  return (
    <article className="metal-red fade-up relative grid min-w-0 gap-6 overflow-hidden rounded-3xl p-6 sm:p-8 lg:grid-cols-[1.2fr_1fr]" data-testid={`minisite-featured-${app.id}`}>
      <div className="min-w-0 space-y-4">
        <NoveltyBanner app={app} teaser testId="minisite-featured-novelty" />
        <MegaScroller mega={app.mega} testId="minisite-featured-mega" />
        <div className="flex flex-wrap gap-2"><MinisiteBadge testId="minisite-featured-badge" /><PlatformBadge app={app} testId="minisite-featured-platform" /></div>
        <Link to={`/apps/${app.id}`} className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2" data-testid="minisite-featured-link">
          <AppIcon app={app} size="h-14 w-14 sm:h-20 sm:w-20" px={80} />
          <h3 className="min-w-0 flex-1 basis-44 [overflow-wrap:anywhere] font-display text-base font-black uppercase tracking-wide text-white sm:text-xl">{app.name}</h3>
          <ChevronRight className="h-5 w-5 shrink-0 text-[var(--gold)] rtl:-scale-x-100" />
        </Link>
        {app.description && <p className="line-clamp-4 break-words [overflow-wrap:anywhere] text-sm text-red-50/85">{app.description}</p>}
        <MinisiteTags tags={tags} testId="minisite-featured-tags" />
        <div className="flex flex-wrap items-center gap-3 text-xs font-semibold text-[var(--gold-bright)]">
          <span className="inline-flex items-center gap-1.5" data-testid="minisite-featured-downloads"><Download className="h-3.5 w-3.5" /> {fmtDownloads(app.downloads)}</span>
          <RatingBadge rating={app.rating} testId="minisite-featured-rating" />
        </div>
        <PlatformDownload app={app} testId="minisite-featured-download-btn" />
      </div>
      {features.length > 0 && (
        <ul className="min-w-0 space-y-2 self-center rounded-2xl border border-white/10 bg-black/30 p-4" data-testid="minisite-featured-features">
          <li className="text-xs uppercase tracking-wider text-neutral-400">{t("mini.features")}</li>
          {features.slice(0, 8).map((f, i) => <li key={i} className="break-words [overflow-wrap:anywhere] text-sm text-neutral-100">— {f}</li>)}
        </ul>
      )}
    </article>
  );
};

export const MinisiteSection = ({ apps }) => {
  const { t } = useI18n();
  if (!apps?.length) return null;
  return (
    <section className="mx-auto max-w-7xl px-4 pt-16 sm:px-8" data-testid="home-minisites-section">
      <div className="mb-8 min-w-0">
        <p className="font-display text-[0.7rem] uppercase tracking-[0.3em] text-emerald-300">{t("mini.eyebrow")}</p>
        <h2 className="mt-2 break-words font-display text-base font-bold uppercase tracking-wider text-white md:text-lg">{t("mini.title")}</h2>
        <p className="mt-2 max-w-2xl break-words text-sm text-neutral-400">{t("mini.lead")}</p>
        <Link to="/espace-client" className="mt-3 inline-flex max-w-full items-center gap-1.5 break-words text-sm font-semibold text-emerald-300 hover:underline" data-testid="home-client-space-link">
          {t("client.space")} <ChevronRight className="h-4 w-4 shrink-0 rtl:-scale-x-100" />
        </Link>
      </div>
      {apps.length === 1 ? <Featured app={apps[0]} /> : <AppGrid apps={apps} />}
    </section>
  );
};
